import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { apiClient } from '@/lib/api.ts'

export interface Tenant {
  id: string
  code: string
  name: string
  description?: string
  tanssCompanyId?: string
  tanssCompanyName?: string
  isActive: boolean
  isPrimary: boolean
}

interface TenantState {
  tenants: Tenant[]
  /** Legacy single-select (fuer X-Tenant-Id Header und SAP-Seiten) */
  activeTenantId: string | null
  /** Multi-select: IDs der global aktiven Mandanten (≥1 immer aktiv) */
  activeTenantIds: string[]
  loaded: boolean
  loading: boolean

  fetchTenants: () => Promise<void>
  setActiveTenant: (tenantId: string) => void
  getActiveTenant: () => Tenant | null
  toggleTenant: (tenantId: string) => void
  setActiveTenantIds: (ids: string[]) => void
}

export const useTenantStore = create<TenantState>()(
  persist(
    (set, get) => ({
      tenants: [],
      activeTenantId: null,
      activeTenantIds: [],
      loaded: false,
      loading: false,

      fetchTenants: async () => {
        if (get().loading) return
        set({ loading: true })
        try {
          const { data } = await apiClient.get<Tenant[]>('/tenants')
          const tenants = data.filter((t) => t.isActive)
          set({ tenants, loaded: true, loading: false })

          const { activeTenantId, activeTenantIds } = get()

          const validIds = new Set(tenants.map((t) => t.id))

          // Legacy single-select: auto-select primary or first, reset if no longer valid
          if ((!activeTenantId || !validIds.has(activeTenantId)) && tenants.length > 0) {
            const primary = tenants.find((t) => t.isPrimary)
            set({ activeTenantId: primary?.id ?? tenants[0]!.id })
          }

          // Multi-select: initialize on first load, or reconcile against the authoritative
          // server list — remove tenants the user can no longer access, keep the rest.
          // We do NOT auto-add tenants the user has not selected: doing so would silently
          // override an explicit deselection every time fetchTenants is called (which
          // happens on every navigation via TenantContextBar).
          if (activeTenantIds.length === 0) {
            set({ activeTenantIds: tenants.map((t) => t.id) })
          } else {
            const cleaned = activeTenantIds.filter((id) => validIds.has(id))
            set({ activeTenantIds: cleaned.length > 0 ? cleaned : tenants.map((t) => t.id) })
          }
        } catch {
          set({ loading: false })
        }
      },

      setActiveTenant: (tenantId: string) => {
        set({ activeTenantId: tenantId })
      },

      getActiveTenant: () => {
        const { tenants, activeTenantId } = get()
        return tenants.find((t) => t.id === activeTenantId) ?? null
      },

      toggleTenant: (tenantId: string) => {
        const { activeTenantIds, activeTenantId } = get()
        if (activeTenantIds.includes(tenantId)) {
          if (activeTenantIds.length <= 1) return // mind. 1 muss aktiv bleiben
          const next = activeTenantIds.filter((id) => id !== tenantId)
          set({
            activeTenantIds: next,
            activeTenantId: activeTenantId === tenantId ? (next[0] ?? null) : activeTenantId,
          })
        } else {
          set({ activeTenantIds: [...activeTenantIds, tenantId] })
        }
      },

      setActiveTenantIds: (ids: string[]) => {
        if (ids.length === 0) return // mind. 1 muss aktiv bleiben
        const { activeTenantId } = get()
        set({
          activeTenantIds: ids,
          activeTenantId: ids.includes(activeTenantId ?? '') ? activeTenantId : (ids[0] ?? null),
        })
      },
    }),
    {
      name: 'tenant-storage',
      partialize: (state) => ({
        activeTenantId: state.activeTenantId,
        activeTenantIds: state.activeTenantIds,
      }),
    },
  ),
)
